import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteRequest() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [quote, setQuote] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/quote-request/${token}`)
      .then(r => { setInfo(r.data); if (r.data.status === "submitted") setDone(true); })
      .catch(() => setInfo(false));
  }, [token]);

  const submit = async () => {
    if (!quote.trim()) { setError("Please write a short quote first"); return; }
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/public/quote-request/${token}`, { quote: quote.trim(), author: author.trim() });
      setDone(true);
    } catch (e) { setError(e?.response?.data?.detail || "Could not submit — please try again"); }
    finally { setBusy(false); }
  };

  if (info === null) return <div className="min-h-screen bg-app flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  if (info === false) return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6">
      <div className="text-center" data-testid="quote-not-found">
        <div className="font-display text-2xl font-bold">This quote link isn't valid</div>
        <p className="text-slate-400 text-sm mt-2">It may have expired — ask your team for a fresh link.</p>
      </div>
    </div>
  );

  const accent = info.accent_color || "#fbbf24";
  return (
    <div className="min-h-screen bg-app text-white flex items-start justify-center p-6 pt-20">
      <div className="w-full max-w-xl" data-testid="quote-request-page">
        <div className="text-xs uppercase tracking-widest font-medium mb-1" style={{ color: accent }}>{info.agency_name}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Share a quick win quote</h1>
        <p className="text-slate-400 text-sm mt-2 mb-6">
          30 seconds — tell future clients what working with {info.agency_name} on <b className="text-slate-200">{info.campaign_name}</b> has been like. Your quote goes live on the campaign dashboard.
        </p>
        {done ? (
          <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.06] p-6 flex items-center gap-3" data-testid="quote-thanks">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shrink-0"><Check className="w-5 h-5 text-slate-900" /></div>
            <div>
              <div className="font-semibold">Thank you — your quote is live!</div>
              <div className="text-xs text-slate-400 mt-0.5">It's now part of the rotation on the campaign dashboard.</div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Your quote</label>
              <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={3} maxLength={300}
                placeholder='"They doubled our small-dollar donations in six weeks."' data-testid="quote-input"
                className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none resize-none"
                style={{ borderColor: quote ? `${accent}66` : undefined }} />
              <div className="text-[10px] text-slate-600 text-right mt-1">{quote.length}/300</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Your name & role (optional)</label>
              <input value={author} onChange={(e) => setAuthor(e.target.value)} maxLength={80}
                placeholder="Jane Doe, Campaign Manager" data-testid="quote-author-input"
                className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none" />
            </div>
            {error && <div className="text-xs text-rose-300" data-testid="quote-error">{error}</div>}
            <button onClick={submit} disabled={busy} data-testid="quote-submit-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm text-slate-900 disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ background: accent }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit quote
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
